// src/pages/MapSearch.js
// Page carte - bouton retour flottant en bas - NE PAS MODIFIER

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from '../utils/leafletConfig';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import LocationPermissionModal from '../components/shared/LocationPermissionModal';
import { formatPriceForMap } from '../utils/formatPrice';
import { APP_CONFIG } from '../constants/config';

const RecenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
    }
  }, [center, zoom, map]);
  return null;
};

const MapSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [visibleListings, setVisibleListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [userPosition, setUserPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([APP_CONFIG.DEFAULT_LAT, APP_CONFIG.DEFAULT_LNG]);
  const [searching, setSearching] = useState(false);
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);

  useEffect(() => {
    const loadDefault = async () => {
      try {
        const result = await apiClient.get(
          `/endpoints/listings.php?action=nearby&lat=${APP_CONFIG.DEFAULT_LAT}&lng=${APP_CONFIG.DEFAULT_LNG}&radius=30`
        );
        setVisibleListings(result.listings || []);
      } catch (err) {
        setVisibleListings([]);
      }
    };
    loadDefault();
  }, []);

  const categoryChips = [
    { id: 'all', icon: '🔎', label: t('common.all') },
    { id: 'sejours', icon: '🏨', label: t('categories.sejours') },
    { id: 'locations', icon: '🏠', label: t('categories.locations') },
    { id: 'immobilier', icon: '🌍', label: t('categories.immobilier') },
    { id: 'services', icon: '🛎️', label: t('categories.services') },
  ];

  const fetchListings = useCallback(async (lat, lng, category) => {
    try {
      const params = new URLSearchParams({
        action: 'nearby',
        lat,
        lng,
        radius: 30,
        ...(category && category !== 'all' ? { category } : {}),
      }).toString();
      const result = await apiClient.get(`/endpoints/listings.php?${params}`);
      return result.listings || [];
    } catch (err) {
      return [];
    }
  }, []);

  const handleSearch = useCallback(() => {
    if (!navigator.geolocation) {
      setShowPermissionGuide(true);
      return;
    }

    setSearching(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserPosition({ lat: latitude, lng: longitude });
        setMapCenter([latitude, longitude]);
        const listings = await fetchListings(latitude, longitude, activeCategory);
        setVisibleListings(listings);
        setSearching(false);
      },
      (error) => {
        setSearching(false);
        if (error.code === 1) {
          setShowPermissionGuide(true);
        }
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [activeCategory, fetchListings]);

  const handleCategorySelect = async (categoryId) => {
    setActiveCategory(categoryId);
    const lat = userPosition ? userPosition.lat : mapCenter[0];
    const lng = userPosition ? userPosition.lng : mapCenter[1];
    const listings = await fetchListings(lat, lng, categoryId);
    setVisibleListings(listings);
  };

  const createPriceIcon = (listing) => {
    const priceLabel = formatPriceForMap(listing.price, listing.price_type || listing.priceType);
    const isSelected = selectedListing?.id === listing.id;
    return L.divIcon({
      className: 'price-marker',
      html: `<div style="
        background-color: ${isSelected ? theme.gold : theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A'};
        color: ${isSelected ? (theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A') : theme.gold};
        border: 2px solid ${theme.gold};
        border-radius: 20px;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.35);
        font-family: -apple-system, sans-serif;
      ">${listing.quartier} · ${priceLabel}</div>`,
      iconSize: [120, 32],
      iconAnchor: [60, 16],
    });
  };

  const userIcon = L.divIcon({
    className: 'user-marker',
    html: `<div style="
      width: 16px; height: 16px; border-radius: 50%;
      background-color: #4299E1; border: 3px solid #FFFFFF;
      box-shadow: 0 0 0 4px rgba(66,153,225,0.3);
    "></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

  const styles = {
    container: { display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative', backgroundColor: theme.bg },
    topBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '16px',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      background: theme.mode === 'light'
        ? 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0))'
        : 'linear-gradient(to bottom, rgba(10,14,26,0.95), rgba(10,14,26,0))',
      paddingBottom: '32px',
    },
    countBadge: {
      padding: '8px 14px',
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      color: theme.textSecondary,
      boxShadow: theme.shadow,
    },
    mapWrapper: { flex: 1, width: '100%', height: '100%' },
    // Bouton retour flottant, toujours visible en bas à gauche
    backButton: {
      position: 'fixed',
      bottom: '76px',
      left: '16px',
      width: '52px',
      height: '52px',
      borderRadius: '50%',
      backgroundColor: theme.textPrimary,
      color: theme.bg,
      border: 'none',
      fontSize: '22px',
      fontWeight: '700',
      cursor: 'pointer',
      zIndex: 1001,
      boxShadow: theme.shadow,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    bottomPanel: {
      position: 'fixed',
      bottom: '76px',
      left: '84px',
      right: '16px',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    chipsRow: {
      display: 'flex',
      gap: '8px',
      overflowX: 'auto',
      scrollbarWidth: 'none',
    },
    chip: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '20px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, boxShadow: theme.shadow },
    chipActive: { backgroundColor: theme.gold, color: theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A', border: `1px solid ${theme.gold}` },
    searchButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px',
      backgroundColor: theme.textPrimary,
      color: theme.bg,
      border: 'none',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      boxShadow: theme.shadow,
    },
    selectedCard: {
      position: 'fixed',
      bottom: '76px',
      left: '84px',
      right: '16px',
      backgroundColor: theme.bgCard,
      borderRadius: '12px',
      padding: '12px',
      display: 'flex',
      gap: '12px',
      zIndex: 1000,
      border: `1px solid ${theme.goldBorder}`,
      cursor: 'pointer',
      boxShadow: theme.shadow,
    },
    cardImage: { width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 },
    cardContent: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px', minWidth: 0 },
    cardTitle: { fontSize: '14px', fontWeight: '600', color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    cardLocation: { fontSize: '12px', color: theme.textSecondary },
    cardPrice: { fontSize: '14px', fontWeight: '700', color: theme.gold },
    closeCardBtn: { position: 'absolute', top: '8px', right: '8px', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.5)', border: 'none', color: '#FFFFFF', fontSize: '14px', cursor: 'pointer' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Carte des annonces | Fynho</title>
        <meta name="description" content="Explorez les annonces immobilières à proximité sur la carte au Cameroun." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div style={styles.topBar}>
        <div style={styles.countBadge}>
          {visibleListings.length} {visibleListings.length > 1 ? t('home.listings') : t('home.listing')}
        </div>
      </div>

      <div style={styles.mapWrapper}>
        <MapContainer center={mapCenter} zoom={APP_CONFIG.DEFAULT_MAP_ZOOM} style={{ width: '100%', height: '100%' }}>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <RecenterMap center={mapCenter} zoom={14} />

          {userPosition && (
            <Marker position={[userPosition.lat, userPosition.lng]} icon={userIcon}>
              <Popup>Vous êtes ici</Popup>
            </Marker>
          )}

          {visibleListings.map((listing) => (
            listing.lat && listing.lng ? (
              <Marker
                key={listing.id}
                position={[parseFloat(listing.lat), parseFloat(listing.lng)]}
                icon={createPriceIcon(listing)}
                eventHandlers={{ click: () => setSelectedListing(listing) }}
              >
                <Popup>
                  <strong>{listing.title}</strong>
                  <br />
                  {listing.quartier} — {formatPriceForMap(listing.price, listing.price_type || listing.priceType)}
                  {listing.distanceKm != null && (
                    <>
                      <br />
                      {listing.distanceKm} km
                    </>
                  )}
                </Popup>
              </Marker>
            ) : null
          ))}
        </MapContainer>
      </div>

      {/* Bouton retour flottant, toujours visible */}
      <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>

      {!selectedListing && (
        <div style={styles.bottomPanel}>
          <div style={styles.chipsRow} className="hide-scrollbar">
            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
            {categoryChips.map((chip) => (
              <div
                key={chip.id}
                style={{ ...styles.chip, ...(activeCategory === chip.id ? styles.chipActive : {}) }}
                onClick={() => handleCategorySelect(chip.id)}
              >
                <span>{chip.icon}</span>
                {chip.label}
              </div>
            ))}
          </div>

          <button style={styles.searchButton} onClick={handleSearch} disabled={searching}>
            🧭 {searching ? t('common.loading') : t('home.useLocation')}
          </button>
        </div>
      )}

      {selectedListing && (
        <div style={styles.selectedCard} onClick={() => navigate(`/listing/${selectedListing.slug}`)}>
          <button
            style={styles.closeCardBtn}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedListing(null);
            }}
          >
            ✕
          </button>
          <img src={selectedListing.images?.[0] || '/images/placeholder.png'} alt={selectedListing.title} style={styles.cardImage} />
          <div style={styles.cardContent}>
            <div style={styles.cardTitle}>{selectedListing.title}</div>
            <div style={styles.cardLocation}>
              📍 {selectedListing.quartier}, {selectedListing.city}
              {selectedListing.distanceKm != null && ` · ${selectedListing.distanceKm} km`}
            </div>
            <div style={styles.cardPrice}>
              {formatPriceForMap(selectedListing.price, selectedListing.price_type || selectedListing.priceType)}
            </div>
          </div>
        </div>
      )}

      <LocationPermissionModal
        open={showPermissionGuide}
        onClose={() => setShowPermissionGuide(false)}
      />
    </div>
  );
};

export default MapSearch;